import React, {useState} from 'react';
import {Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import NetworkDashboardShell from '../components/NetworkDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, rowsFrom, rowMatches, cellText} from '../networkData';
import {isScopeSuppressed} from '../../DashboardCommon/dashboardCards';
import {FAMILY_CODE_BY_NAME} from '../../DashboardCommon/priorityFamilies';
import {NAVY, PageHeader, headCell, bodyCell} from '../components/widgets.comp';
import {EvidenceCell, RecommendationCell} from '../../DashboardCommon/RecommendationCells.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

/* Slide 35 — Recommended System Interventions. Same Country/Region/School
   rows and blank-cell convention as the Learning Program page (slide 33) —
   see that file's doc comment. No button on this slide (no Take Action —
   same "System Intervention has no button" precedent as School Dashboard's
   identical page, confirmed by src/pages/DQPro/PerformanceSummary/
   PerformanceSummary.page.jsx's own "No Take Action here per the mockup").
   "Standard action families" come from the card (see below). */
/* Live rows: SND-35-02 → SND.S_15.C_2 (PriorityTableCard), in this page's own
   layout. The card sends Country, Region ("—" for a network) and School.
   Every column sorts and filters from its header (QA 9 Oct 2026 — the slide's
   Country/Region/School drop-down bar is gone); the search box matches any cell. */
/* "Standard Action Families" are the families the card itself lists
   (filters.action_family) — only those that apply to this network's recommendations.
   Selecting one filters the table to the recommendations in that family: each
   row carries its family codes (row_meta.families, P1 Data Map SD-36-01 /
   SND-35-02), named as in the Priority Actions guide (Methodology → How
   Priority Actions Are Triggered). Nothing selected shows every row. The
   name → code map is DashboardCommon/priorityFamilies. */
const FamilyFilterChip = ({label, active, onClick}) => (
    <Box
        onClick={onClick}
        sx={{
            cursor: 'pointer', fontSize: '12.5px', fontWeight: 700, whiteSpace: 'nowrap',
            px: 1.75, py: 0.75, borderRadius: '8px',
            border: `1px solid ${active ? NAVY : 'var(--brand-border, #E5E7EB)'}`,
            backgroundColor: active ? NAVY : 'var(--brand-surface, #ffffff)',
            color: active ? '#ffffff' : NAVY,
            '&:hover': {borderColor: NAVY},
        }}
    >
        {label}
    </Box>
);

const COLUMNS = [
    {key: 'country', label: 'Country', type: 'text'},
    {key: 'region', label: 'Region', type: 'text'},
    {key: 'school', label: 'School', type: 'text'},
    // The slide renames the card's Baseline Evidence column (Ian, 1 Oct). Both list cells sort by their text; no checklist.
    {key: 'evidence', label: 'Areas of Weakness', type: 'text', filterable: false, accessor: (r) => cellText(r._raw?.evidence)},
    {key: 'recommendation', label: 'Recommended Actions', type: 'text', filterable: false, accessor: (r) => cellText(r._raw?.recommendation)},
];

const RecommendationTable = ({rows, emptyText = 'No recommendations yet.', query, onQuery}) => {
    const t = useTableControls(rows, COLUMNS);
    return (
        <>
            <TableToolbar {...t} query={query} onQuery={onQuery} placeholder="Search by country, region, school…"/>
            <TableContainer sx={{
                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {COLUMNS.map((c, i) => (
                                <SortFilterHeaderCell key={c.key} {...t.headerProps(c.key)} sx={{...headCell, textAlign: 'left', ...(i === 0 ? {pl: 2.5} : {})}}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>{emptyText}</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((row, i) => (
                            <TableRow key={`${row.country}-${row.region}-${row.school}-${rows.indexOf(row)}`} sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.country}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.region}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.school}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}><EvidenceCell value={row._raw?.evidence} fallback={row.evidence}/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}><RecommendationCell row={row}/></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

// Rows for units under the n floor are not built upstream; a dashboard whose own population is under it says so.
const SUPPRESSED_EMPTY = 'Priority actions are not shown for groups of fewer than 10 students.';
const NO_MATCH = 'No row matches the search and filters.';

const PRIORITY_ACTIONS_SI_SECTION_CODE = 'S_15';
const CARD_SECTIONS = [PRIORITY_ACTIONS_SI_SECTION_CODE];

const PriorityActionsSystemInterventionPage = () => {
    const [activeFamily, setActiveFamily] = useState(null);
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const cardForFamilies = findCard(dashboardData, PRIORITY_ACTIONS_SI_SECTION_CODE, 'C_2');
    const ACTION_FAMILIES = cardForFamilies?.filters?.action_family || [];
    const card = findCard(dashboardData, PRIORITY_ACTIONS_SI_SECTION_CODE, 'C_2');
    const [query, setQuery] = useState('');
    const rows = rowsFrom(card, {
        country: 'Country', region: 'Region', school: 'School',
        evidence: 'Baseline Evidence', recommendation: 'Recommended Actions',
    });
    const q = query.trim().toLowerCase();
    const familyCode = activeFamily ? FAMILY_CODE_BY_NAME[activeFamily] : null;
    const shown = rows
        .filter((r, i) => !familyCode || (card?.row_meta?.[i]?.families || []).includes(familyCode))
        .filter((r) => rowMatches(r, q));

    if (loading) {
        return (
            <NetworkDashboardShell>
                <CircularLoader />
            </NetworkDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <NetworkDashboardShell>
                <NoDataAvailable />
            </NetworkDashboardShell>
        );
    }

    return (
        <NetworkDashboardShell>
            <PageHeader
                title="Recommended System Interventions"
                subtitle="The crosswalk converts priority COS areas into standard action families and concrete interventions."
            />

            {ACTION_FAMILIES.length > 0 && (
            <>
            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>
                Standard Action Families
            </Typography>
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 3}}>
                {ACTION_FAMILIES.map((f) => (
                    <FamilyFilterChip key={f} label={f} active={activeFamily === f} onClick={() => setActiveFamily(activeFamily === f ? null : f)}/>
                ))}
            </Box>
            </>
            )}

            <RecommendationTable
                rows={shown}
                query={query}
                onQuery={setQuery}
                emptyText={rows.length ? NO_MATCH : (isScopeSuppressed(dashboardData) ? SUPPRESSED_EMPTY : undefined)}
            />
        </NetworkDashboardShell>
    );
};

export default PriorityActionsSystemInterventionPage;
